import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosRequestConfig } from "axios";
import { APIRequestStatus } from "common-ts/dist/models/APIRequestStatus";
import { Order, OrderItem, OrderOnlyList } from "common-ts/dist/models/Orders";
import OrderItemLabels, { OrderShippingLabel, OrderCourierLabel, StockLabel, WorkInputForm, WorkInputFormPages, OrderItemLabelsPages, OrderShippingLabelPages, OrderCourierLabelPages } from "src/content/applications/Orders/OrderShippingLabel";
import { pdf, Document } from '@react-pdf/renderer';
import Midstock from "common-ts/dist/models/Midstock";
import { CalculateClothingMeasurementsFromBodyMeasurements, ClothingMeasurementDetailList } from "common-ts/dist/models/UserMeasurements";
import { RegularSizeValue } from "common-ts/dist/models/Cart";

export const currentFormVersion = 'v3.0';

const initialState : OrderOnlyList = {
    ordersDictionary: {},
    ordersList: [],

    ordersListProduction: [],
    opnamePengirimanStatusDict: {},

    pengalihanList: [],
    perbaikanList: [],

    status: APIRequestStatus.Idle,
    error: null
}

export const updateOpnamePengirimanStatusDictNoRefresh = createAsyncThunk('ordersList/updateOpnamePengirimanStatusDictNoRefresh', async (arg: any, thunkAPI) => {

    thunkAPI.dispatch(updateLocalOpnamePengirimanStatusDict(arg));

    const response = await axios({
        method: 'post',
        url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/fin-updater',
        data: JSON.stringify({
            opnamePengirimanStatusDict: arg // Updates the diff
        })
    });
      
    return response.data
});

export const updateOpnamePengirimanStatusDict = createAsyncThunk('ordersList/updateOpnamePengirimanStatusDict', async (arg: any, thunkAPI) => {

    thunkAPI.dispatch(updateLocalOpnamePengirimanStatusDict(arg));

    const response = await axios({
        method: 'post',
        url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/fin-updater',
        data: JSON.stringify({
            opnamePengirimanStatusDict: arg // Updates the diff
        })
    });

    thunkAPI.dispatch(fetchRefreshOpnamePengiriman());
      
    return response.data
});

export const fetchRefreshOpnamePengiriman = createAsyncThunk('ordersList/fetchRefreshOpnamePengiriman', async () => {
    const reqBody = {
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/orders-all',
    };

    reqBody['params'] = {
        "isopnamepengiriman": "yes"
    };

    const response = await axios(reqBody);
    return response.data
})

export const fetchOrdersListProduction = createAsyncThunk('ordersList/fetchOrdersListProduction', async (arg: void, thunkAPI) => {
    const reqBody = {
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/orders-all',
    };

    reqBody['params'] = {
        "isproductionview": "yes"
    };

    const response = await axios(reqBody);

    thunkAPI.dispatch(fetchPerbaikanList());

    return response.data
})

// Manually pull the AWB from KiriminAja for a single courierOrderId. The orders-all
// endpoint, when given a `courierorder` param, calls KiriminAja /api/mitra/tracking,
// extracts details.awb and writes it back onto the order (see ordersAllGet.js). This is
// the fallback for when KiriminAja fails to deliver the processed_packages webhook that
// normally sets courierAwb. After the sync we reload the production list so the AWB shows.
export const fetchCourierAwbSync = createAsyncThunk('ordersList/fetchCourierAwbSync', async (courierOrderId: string, thunkAPI) => {
    if (!courierOrderId) {
        return {};
    }

    const response = await axios({
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/orders-all',
        params: {
            "courierorder": courierOrderId
        }
    });

    // Reload the production list so the freshly-synced AWB is reflected in the UI.
    thunkAPI.dispatch(fetchOrdersListProduction());

    return response.data
})

export const fetchPerbaikanList = createAsyncThunk('ordersList/fetchPerbaikanList', async () => {
    const reqBody = {
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/orders-all',
    };

    reqBody['params'] = {
        "isperbaikanquery": "yes"
    };

    const response = await axios(reqBody);
    return response.data
})

export const fetchPengalihanList = createAsyncThunk('ordersList/fetchPengalihanList', async () => {
    const reqBody = {
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/orders-all',
    };

    reqBody['params'] = {
        "ispengalihanquery": "yes"
    };

    const response = await axios(reqBody);
    return response.data
})

export const fetchOrdersListFast = createAsyncThunk('ordersList/fetchOrdersListFast', async () => {
    const reqBody = {
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/orders-all',
    };

    reqBody['params'] = {
        "isfastreq": "yes"
    };

    const response = await axios(reqBody);
    return response.data
})
export const fetchOrdersList = createAsyncThunk('ordersList/fetchOrdersList', async (arg: string, thunkAPI) => {
    thunkAPI.dispatch(fetchOrdersListFast());

    const reqBody = {
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/orders-all',
    };

    if (arg) {
        reqBody['params'] = {
            "courierorder": arg
        };
    }

    const response = await axios(reqBody);
    return response.data
})

export function getClothingMeasurementsForItem(cartProductItem: any) {
    var clothingMeasurementsForItem = cartProductItem?.userMeasurementsForItem?.adjustedMeasurements && cartProductItem?.clothingType && cartProductItem?.fitType ? 
    CalculateClothingMeasurementsFromBodyMeasurements(cartProductItem.clothingType, cartProductItem.fitType, cartProductItem.userMeasurementsForItem.adjustedMeasurements, RegularSizeValue.L) : 
    {};

    // Round to the nearest tenth
    var roundedClothingMeasurementsForItem = {};
    for (const [key, value] of Object.entries(clothingMeasurementsForItem)) {
        roundedClothingMeasurementsForItem[key] = Math.round(10 * value) / 10;
    }

    const clothMeasurementsList = [];

    const measKeyList = Object.keys(clothingMeasurementsForItem)?.sort();
    for (let measKey of measKeyList) {
        const measValue = clothingMeasurementsForItem?.[measKey];
        const measValueRounded = roundedClothingMeasurementsForItem?.[measKey];
        const measName = ClothingMeasurementDetailList?.[measKey]?.clothingMeasurementName ?? '';
        const tol1 = measValue > 30 ? 0.8 : 0.5;
        const tol2 = measValue > 30 ? 1.9 : 1.0;

        clothMeasurementsList.push({
            clothingMeasurementKey: measKey,
            clothingMeasurementName: measName,
            clothingMeasurementValue: measValue,
            clothingMeasurementValueRounded: measValueRounded,
            clothingMeasurementTol1: tol1,
            clothingMeasurementTol2: tol2,
        })
    }

    return clothMeasurementsList;
}

export function getClothingMeasurementsForItemForOrderList(orderList: Order[]) {
    const workOrderItemFulfillList = [];
    for (let orderData of orderList) {
        let orderId = orderData.orderId;
        let counter = 0;
        for (let cartProductItem of orderData.cartProductList) {
            for (let i = 0; i <  cartProductItem.amount; i = i + 1) {
                const clothMeasurementsObj = {
                    orderIdItemNumber: orderId + '+' + ("000" + counter).slice(-3),
                    clothMeasurementsList: getClothingMeasurementsForItem(cartProductItem),
                    formVersion: currentFormVersion,
                }

                workOrderItemFulfillList.push(clothMeasurementsObj);
                counter = counter + 1;
            }
        }
    }

    return workOrderItemFulfillList;
}

const populateItemLabelInputData = async ({inputData, arg}) => {

    // Fetch user personal input data
    var config: AxiosRequestConfig = {
        method: 'get',
        url: 'https://ojm2bfbydd.execute-api.ap-southeast-1.amazonaws.com/UserAPIProd/personalsizeinput?usersub=' + arg.userSub,
        headers: { 
        }
    };
    const response = await axios(config);
    if (response?.data?.success) {
        inputData.userPersonalInputData = response.data.content;

        // After fetching successfully, we update user personal input data the hasBeenPrinted in server
        var config2: AxiosRequestConfig = {
            method: 'post',
            url: 'https://ojm2bfbydd.execute-api.ap-southeast-1.amazonaws.com/UserAPIProd/personalsizeinput',
            headers: {
            },
            data: {
                userSub: inputData.userPersonalInputData.userSub,
                hasBeenPrinted: true,
                hasBeenPrintedDateTime: new Date().toISOString(),
            }
        };

        // We wait until update finishes and succeeds before making the pdf
        const response2 = await axios(config2);
    }

    // Fetch inventory request data
    var config3: AxiosRequestConfig = {
        method: 'get',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/inventoryrequest/single',
        params: {
            "orderid": arg?.orderId ? arg.orderId : ""
        },
        headers: { 
        }
    };
    const response3 = await axios(config3);
    if (response3?.data?.content !== undefined) {
        inputData.inventoryRequestData = response3.data.content;
    }

    return inputData;
}

const populateShipmentLabelInputData = async ({inputData, arg}) => {

    // Fetch user personal input data
    var config: AxiosRequestConfig = {
        method: 'get',
        url: 'https://ojm2bfbydd.execute-api.ap-southeast-1.amazonaws.com/UserAPIProd/personalsizeinput?usersub=' + arg.userSub,
        headers: { 
        }
    };
    const response = await axios(config);
    if (response?.data?.success) {
        inputData.userPersonalInputData = response.data.content;
    }

    return inputData;
}

export const registerBatchAndFetchAllPrintPdf = createAsyncThunk('ordersList/fetchAllPrintPdf', async (arg: Order[], thunkAPI) => {

    const response = await axios({
        method: 'post',
        url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/order', // Endpoint for order update
        data: {
            orderEditList: arg.map((orderObj) => ({
                userSub: orderObj.userSub,
                creationDateTime: orderObj.creationDateTime,
            }))
        }
    });

    if (response.data.success) {
        thunkAPI.dispatch(fetchAllPrintPdf(arg));
        thunkAPI.dispatch(fetchOrdersList(""));
    }
})

export const fetchAllPrintPdf = createAsyncThunk('ordersList/fetchAllPrintPdf', async (arg: Order[], thunkAPI) => {

    const dt = new Date().toISOString();

    const state: any = thunkAPI.getState();
    const midstockList = state.midstockList.midstockList;

    // Register measurements for work input
    const resultMeasList = getClothingMeasurementsForItemForOrderList(arg);

    var config2: AxiosRequestConfig = {
        method: 'post',
        url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/work-order-item-fulfill',
        data: JSON.stringify({
          processAction: "BATCH_UPDATE_MEAS_AND_PRINT",
          itemList: resultMeasList
        })
    };
    await axios(config2);

    const totalInputData: any[] = [];
    for (let orderDataObj of arg) {
        let labelItemInputData: any = {
            orderData: orderDataObj,
            userPersonalInputData: {},
            inventoryRequestData: {},
        };
        
        labelItemInputData = await populateItemLabelInputData({inputData: labelItemInputData, arg: orderDataObj});

        totalInputData.push(labelItemInputData);
    }


    // WORK INPUT PAGES
    let combinedPagesWorkInput = [];

    for (let inputObj of totalInputData) {
        combinedPagesWorkInput = combinedPagesWorkInput.concat(WorkInputFormPages({
            inputData: inputObj,
            midstockList: midstockList,
        }));
    }

    const combinedDocumentWorkInput = (
        <Document>
            {combinedPagesWorkInput}
        </Document>
    );

    const blob = pdf(combinedDocumentWorkInput).toBlob();

    blob.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Work_Input_Form_Combined_" + dt + ".pdf";
      link.click();
    });

    // PERSONALIZED LABEL
    let combinedPagesItemLabel = [];

    for (let inputObj of totalInputData) {
        combinedPagesItemLabel = combinedPagesItemLabel.concat(OrderItemLabelsPages({
            inputData: inputObj
        }));
    }

    const combinedDocumentItemLabel = (
        <Document>
            {combinedPagesItemLabel}
        </Document>
    );

    const blob2 = pdf(combinedDocumentItemLabel).toBlob();

    blob2.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Item_Label_Combined_" + dt + ".pdf";
      link.click();
    });

    // ADDRESS LABEL
    let combinedPagesAddress = [];

    for (let inputObj of totalInputData) {
        if (inputObj?.orderData?.paymentStatus === "capture_marketplace") {
            continue;
        }
        combinedPagesAddress = combinedPagesAddress.concat(OrderShippingLabelPages({
            inputData: inputObj,
            isLong: true,
        }));
    }

    const combinedDocumentAddress = (
        <Document>
            {combinedPagesAddress}
        </Document>
    );

    const blob3 = pdf(combinedDocumentAddress).toBlob();

    blob3.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Address_Combined_" + dt + ".pdf";
      link.click();
    });
})

export const fetchWorkInputFormPdf = createAsyncThunk('ordersList/fetchWorkInputFormPdf', async (arg: Order, thunkAPI) => {

    const state: any = thunkAPI.getState();
    const midstockList = state.midstockList.midstockList;

    // Register measurements for work input
    const resultMeasList = getClothingMeasurementsForItemForOrderList([arg]);

    var config2: AxiosRequestConfig = {
        method: 'post',
        url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/work-order-item-fulfill',
        data: JSON.stringify({
          processAction: "BATCH_UPDATE_MEAS_AND_PRINT",
          itemList: resultMeasList
        })
    };
    await axios(config2);
    console.log(resultMeasList);

    var inputData: any = {
        orderData: arg,
        userPersonalInputData: {},
        inventoryRequestData: {},
    }

    inputData = await populateItemLabelInputData({inputData: inputData, arg: arg});

    const blob = pdf(<WorkInputForm inputData={inputData} midstockList={midstockList}/>).toBlob();

    console.log('TEST inputData: ', inputData)

    blob.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Work Input Form " + arg.orderId + ".pdf";
      link.click();
    });
})

export const fetchUserPersonalizedLabelsPdf = createAsyncThunk('ordersList/fetchUserPersonalizedLabelsPdf', async (arg: Order, thunkAPI) => {
    var inputData: any = {
        orderData: arg,
        userPersonalInputData: {},
        inventoryRequestData: {},
    }

    inputData = await populateItemLabelInputData({inputData: inputData, arg: arg});

    const blob = pdf(<OrderItemLabels inputData={inputData}/>).toBlob();

    blob.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Labels for Order " + arg.orderId + ".pdf";
      link.click();
    });
})

export const fetchUserShipmentLabelPdf = createAsyncThunk('ordersList/fetchUserShipmentLabelPdf', async (arg: Order, thunkAPI) => {
    var inputData: any = {
        orderData: arg,
        userPersonalInputData: {},
    }

    inputData = await populateShipmentLabelInputData({inputData: inputData, arg: arg});

    const blob = pdf(<OrderShippingLabel inputData={inputData} isLong={false}/>).toBlob();

    blob.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Labels for Order " + arg.orderId + ".pdf";
      link.click();
    });
})

export const fetchUserShipmentLabelPdfLong = createAsyncThunk('ordersList/fetchUserShipmentLabelPdfLong', async (arg: Order, thunkAPI) => {
    var inputData: any = {
        orderData: arg,
        userPersonalInputData: {},
    }

    inputData = await populateShipmentLabelInputData({inputData: inputData, arg: arg});

    const blob = pdf(<OrderShippingLabel inputData={inputData} isLong={true} />).toBlob();

    blob.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Labels for Order " + arg.orderId + ".pdf";
      link.click();
    });
})

export const fetchUserCourierLabelPdf = createAsyncThunk('ordersList/fetchUserCourierLabelPdf', async (arg: Order, thunkAPI) => {
    var inputData: any = {
        orderData: arg,
    }

    const blob = pdf(<OrderCourierLabel inputData={inputData} />).toBlob();

    blob.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Label Courier for Order " + arg.orderId + ".pdf";
      link.click();
    });
})

export const fetchAllUserCourierLabelPdf = createAsyncThunk('ordersList/fetchAllUserCourierLabelPdf', async (arg: Order[], thunkAPI) => {

    let combinedPagesAddress = [];

    for (let inputObj of arg) {
        combinedPagesAddress = combinedPagesAddress.concat(OrderCourierLabelPages({
            inputData: {
                orderData: inputObj
            },
        }));
    }

    const combinedDocumentAddress = (
        <Document>
            {combinedPagesAddress}
        </Document>
    );

    const blob3 = pdf(combinedDocumentAddress).toBlob();

    const dt = new Date().toISOString();
    blob3.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Resi_Combined_" + dt + ".pdf";
      link.click();
    });
})

export const fetchStockMidstockLabelPdf = createAsyncThunk('ordersList/fetchStockMidstockLabelPdf', async (arg: Midstock, thunkAPI) => {

    const blob = pdf(<StockLabel inputData={{
        name: arg.midstockId,
    }} />).toBlob();

    blob.then(function (blob) {
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = "Labels for Midstock " + arg.midstockId + ".pdf";
      link.click();
    });
})

export const orderSlice = createSlice({
    name: 'ordersList',
    initialState,
    reducers: {
        updateLocalOpnamePengirimanStatusDict(state, action) {
            state.opnamePengirimanStatusDict = {
                ...state.opnamePengirimanStatusDict,
                ...action.payload
            };
        },
    },
    extraReducers(builder) {
      builder
        .addCase(fetchRefreshOpnamePengiriman.pending, (state, action) => {
        })
        .addCase(fetchRefreshOpnamePengiriman.fulfilled, (state, action) => {
            state.opnamePengirimanStatusDict = action.payload.opnamePengirimanStatusDict.content;
        })
        .addCase(fetchRefreshOpnamePengiriman.rejected, (state, action) => {
        })
        .addCase(fetchOrdersListProduction.pending, (state, action) => {
            // state.ordersListProduction = [];
        })
        .addCase(fetchOrdersListProduction.fulfilled, (state, action) => {
            // Do something here after successful fetch
            state.ordersListProduction = action.payload.ordersList;
            state.opnamePengirimanStatusDict = action.payload.opnamePengirimanStatusDict.content;
        })
        .addCase(fetchOrdersListProduction.rejected, (state, action) => {
        })
        .addCase(fetchPerbaikanList.pending, (state, action) => {
            // state.ordersListProduction = [];
        })
        .addCase(fetchPerbaikanList.fulfilled, (state, action) => {
            // Do something here after successful fetch
            state.perbaikanList = action.payload.perbaikanList;
        })
        .addCase(fetchPerbaikanList.rejected, (state, action) => {
        })
        .addCase(fetchPengalihanList.pending, (state, action) => {
            // state.ordersListProduction = [];
        })
        .addCase(fetchPengalihanList.fulfilled, (state, action) => {
            // Do something here after successful fetch
            state.pengalihanList = action.payload.pengalihanList;
        })
        .addCase(fetchPengalihanList.rejected, (state, action) => {
        })
        .addCase(fetchOrdersListFast.pending, (state, action) => {
        })
        .addCase(fetchOrdersListFast.fulfilled, (state, action) => {
            // Do something here after successful fetch
            const fetchTime = new Date().toISOString();
            const orderDetailArray: [] = action.payload.ordersList.map((orderObj) => ({...orderObj, fetchTime: fetchTime}));
            state.ordersList = orderDetailArray;
            state.ordersDictionary = {};

            for (let key in orderDetailArray) {
            const orderDetail: any = orderDetailArray[key];
            state.ordersDictionary[orderDetail.orderId] = orderDetail;
            }
        })
        .addCase(fetchOrdersListFast.rejected, (state, action) => {
        })
        .addCase(fetchOrdersList.pending, (state, action) => {
            state.status = APIRequestStatus.RequestInProgress;
        })
        .addCase(fetchOrdersList.fulfilled, (state, action) => {
            // Do something here after successful fetch
            const fetchTime = new Date().toISOString();
            const orderDetailArray: [] = action.payload.ordersList.map((orderObj) => ({...orderObj, fetchTime: fetchTime}));
            state.ordersList = orderDetailArray;
            state.ordersDictionary = {};

            for (let key in orderDetailArray) {
            const orderDetail: any = orderDetailArray[key];
            state.ordersDictionary[orderDetail.orderId] = orderDetail;
            }

            state.status = APIRequestStatus.Success;
        })
        .addCase(fetchOrdersList.rejected, (state, action) => {
            state.status = APIRequestStatus.Failure;
            // state.error = action.error.message
            console.log("fetch is failure");
        })
        .addCase(fetchUserPersonalizedLabelsPdf.pending, (state, action) => {
        })
        .addCase(fetchUserPersonalizedLabelsPdf.fulfilled, (state, action) => {
        })
        .addCase(fetchUserPersonalizedLabelsPdf.rejected, (state, action) => {
        })
        .addCase(fetchUserShipmentLabelPdf.pending, (state, action) => {
        })
        .addCase(fetchUserShipmentLabelPdf.fulfilled, (state, action) => {
        })
        .addCase(fetchUserShipmentLabelPdf.rejected, (state, action) => {
        })
        .addCase(fetchUserShipmentLabelPdfLong.pending, (state, action) => {
        })
        .addCase(fetchUserShipmentLabelPdfLong.fulfilled, (state, action) => {
        })
        .addCase(fetchUserShipmentLabelPdfLong.rejected, (state, action) => {
        })
        .addCase(fetchUserCourierLabelPdf.pending, (state, action) => {
        })
        .addCase(fetchUserCourierLabelPdf.fulfilled, (state, action) => {
        })
        .addCase(fetchUserCourierLabelPdf.rejected, (state, action) => {
        })
    }
})

export const { updateLocalOpnamePengirimanStatusDict } = orderSlice.actions

export default orderSlice.reducer